import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Eye, EyeOff } from "lucide-react";

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function LoginDialog({ open, onOpenChange }: LoginDialogProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Créer un formulaire HTML pour l'envoi
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/users/sign_in";

    // Ajouter les champs
    const emailInput = document.createElement("input");
    emailInput.type = "hidden";
    emailInput.name = "user[email]";
    emailInput.value = email;

    const passwordInput = document.createElement("input");
    passwordInput.type = "hidden";
    passwordInput.name = "user[password]";
    passwordInput.value = password;

    form.appendChild(emailInput);
    form.appendChild(passwordInput);

    // Ajouter au DOM et soumettre
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const handleGoogleLogin = () => {
    // Ici vous pouvez ajouter votre logique de connexion Google
    console.log("Google login attempt");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md animate-scale-in top-0 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] data-[state=open]:slide-in-from-top sm:data-[state=open]:slide-in-from-top-0">
        <DialogHeader className="text-center space-y-2">
          <DialogTitle className="text-2xl font-display">Se connecter</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Connectez-vous à votre compte Wememberz
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleLogin} className="space-y-6 py-4">
          {/* Formulaire email/mot de passe */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="transition-smooth focus:ring-2 focus:ring-[#f7bbac]/20 focus:border-[#f7bbac]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="transition-smooth focus:ring-2 focus:ring-[#f7bbac]/20 focus:border-[#f7bbac] pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" variant="rainbow" size="lg" className="w-full font-medium" disabled={isLoading}>
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Connexion...
                </div>
              ) : (
                "Se connecter"
              )}
            </Button>
          </div>
        </form>

        <div className="space-y-4">
          {/* Séparateur */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">ou continuer avec</span>
            </div>
          </div>

          {/* Bouton Google */}
          <form method="POST" action="/users/auth/google_oauth2">
            <input type="hidden" name="authenticity_token" value={csrf} />
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-smooth focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-11 px-8"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Se connecter avec Google
            </button>
          </form>

          {/* Lien mot de passe oublié */}
          <div className="text-center">
            <a
              href="/users/password/new"
              className="text-sm text-[#f7bbac] hover:text-[#f7bbac]/80 transition-smooth underline-offset-4 hover:underline"
            >
              Mot de passe oublié ?
            </a>
          </div>

          {/* Lien inscription */}
          <div className="text-center text-sm text-muted-foreground">
            Pas encore de compte ?{" "}
            <a
              href="/users/sign_up"
              className="text-[#f7bbac] hover:text-[#f7bbac]/80 transition-smooth underline-offset-4 hover:underline font-medium"
            >
              S'inscrire
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
